import {ApolloClient} from "apollo-client"
import {InMemoryCache} from "apollo-cache-inmemory"
import {ApolloLink} from "apollo-link"
import {HttpLink} from "apollo-link-http"
import isRPDeal from "@irati/deals/helpers/deals/isRPDeal"
import buildAxiosFetch from "../../axios-fetch"
import graphFetch from "../graph/fetch"
import typeDefs from "./schema"
import errorLink from "./utils/errorLink"

const formatCacheKey = (type: string, id: string) => `${type}@${id}`

/**
 * @description caching SlimDeals by their uuid will cause duplicates to be stored if there are
 * multiple locations for the same deal. Using the deal's location uuid ensures each deal is unique.
 */
const formatSlimDealCacheKey = (locationId: string) => {
  return `SlimDeal@${locationId}`
}

export const cache = new InMemoryCache({
  dataIdFromObject: (object: any) => {
    if (object.__typename === "SlimDeal") {
      return formatSlimDealCacheKey(object.location.uuid)
    }
    if (object.__typename === "Product" || object.__typename === "DealProductSnapshot") {
      return null
    }

    if (object.__typename === "Deal" && isRPDeal(object)) {
      return formatCacheKey(object.__typename, `${object.uuid}@RewardPoint`)
    }
    if (object.__typename === "Budget" || object.__typename === "BudgetUnassigned") {
      return formatCacheKey(object.__typename, `${object.walletId}_${object.uuid}`)
    }
    return !object.uuid && !object.id ? null : formatCacheKey(object.__typename, object.uuid || object.id)
  },
  cacheRedirects: {
    Query: {
      poll: (_, args, {getCacheKey}) =>
        getCacheKey({
          uuid: args.id,
          __typename: "Poll"
        }),
      recognition: (_, args, {getCacheKey}) =>
        getCacheKey({
          uuid: args.id,
          __typename: "Recognition"
        })
    }
  }
})

// omit typename on update https://github.com/apollographql/apollo-client/issues/1564
const omitTypename = (key: string, value: any) => (key === "__typename" ? undefined : value)
const apolloLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
  }
  return forward(operation)
})

const client = new ApolloClient({
  link: apolloLink.concat(errorLink).concat(
    new HttpLink({
      fetch: buildAxiosFetch(graphFetch())
    })
  ),
  cache,
  typeDefs
})

export const unauthedClient = new ApolloClient({
  link: apolloLink.concat(errorLink).concat(
    new HttpLink({
      fetch: buildAxiosFetch(graphFetch({unAuth: true}))
    })
  ),
  cache,
  typeDefs
})

export default client
